/**
 * Logica de la barra de foco del expediente (paso 2 de la reorganizacion).
 *
 * Un secretario abre un expediente para saber que le toca hoy. Hasta ahora
 * tenia que deducirlo scrolleando nueve modulos, un checklist y un control
 * de estado. Estas funciones responden esa pregunta sin que tenga que leer
 * la pantalla entera.
 *
 * Van aparte del componente a proposito: son las reglas de "que sigue" y
 * "cuanto llevas", y se testean sin montar React.
 */

import type { ChecklistItemDTO, ProjectDetailDTO } from "@/lib/api/projects";
import type { FormulationDTO, ModuleKey } from "@/lib/api/formulations";
import { MODULE_META, MODULE_ORDER, PREPARACION_ORDER } from "@/lib/api/formulations";

/** Ancla DOM de un modulo, para que la barra pueda llevar hasta el. */
export function moduleAnchorId(key: string): string {
  return `modulo-${key}`;
}

/** Ancla del checklist de radicacion. */
export const CHECKLIST_ANCHOR_ID = "checklist-radicacion";

/** Ancla del control de cambio de estado (pestana Radicar). */
export const STATUS_CONTROLS_ANCHOR_ID = "cambiar-estado";

/** Ancla de la seccion BPIN (vive en el header, fuera de las pestanas). */
export const BPIN_ANCHOR_ID = "bpin";

/**
 * Las cuatro zonas de trabajo del expediente.
 *
 * No son categorias de contenido sino etapas del trabajo real: primero se
 * formula, despues se radica; las convocatorias y el historial se consultan
 * cuando hacen falta. El orden de las pestanas es el orden del trabajo.
 */
export type ExpedienteTab = "formular" | "radicar" | "convocatorias" | "historial";

export interface NextAction {
  /** Que le toca hacer, en sus palabras. */
  label: string;
  /** id del elemento al que hay que llevarlo. */
  targetId: string;
  /**
   * Pestana donde vive el destino. Sin esto el boton llevaria a un ancla
   * que esta desmontada: con pestanas, el contenido no visible no existe
   * en el DOM.
   */
  tab: ExpedienteTab;
  /** Texto del boton. */
  cta: string;
}

/**
 * Lo unico que el usuario tiene que hacer ahora.
 *
 * Orden de prioridad, que es el orden real del trabajo:
 *
 * 1. Los 6 modulos de identificacion, uno por uno. Sin ellos no hay nada
 *    que preparar ni que radicar.
 * 2. Los 3 de preparacion, que recien se desbloquean con identificacion
 *    completa (ADR-021 regla 5).
 * 3. El checklist de radicacion, contando solo los obligatorios: los
 *    opcionales no bloquean nada y ponerlos aca seria mentirle al usuario
 *    sobre lo que le falta.
 * 4. Radicar: un borrador con todo lo anterior listo no esta "sin
 *    pendientes", le falta marcarse como radicado. Los expedientes
 *    idea-first no tienen checklist por diseno (issue #569), asi que sin
 *    este paso caian directo en la felicitacion.
 * 5. El BPIN: radicado sin BPIN es exactamente lo que el banner de arriba
 *    reclama; la barra no puede decir lo contrario en la misma pantalla. Por
 *    eso no decide con una lista de estados propia: lee `bpin_pending`, que
 *    el backend calcula con la regla del banner (issue #823).
 *
 * `null` cuando no queda nada pendiente — ahi la barra felicita en vez de
 * empujar.
 */
export function computeNextAction(
  project: ProjectDetailDTO,
  formulation: FormulationDTO | null,
  /**
   * Si el plan trae el asistente IA. Sin él, el botón del módulo vacío es
   * "Describir manualmente" y la barra usa ese verbo (issue #629). Por
   * defecto `true`: mientras el plan no carga, el texto de siempre.
   */
  { conIA = true }: { conIA?: boolean } = {},
): NextAction | null {
  if (formulation) {
    const pendiente = findFirstUnconfirmed(formulation, [...MODULE_ORDER, ...PREPARACION_ORDER]);
    if (pendiente) {
      const label = MODULE_META[pendiente]?.label ?? pendiente;
      const tieneContenido = Boolean(formulation.modules[pendiente]?.content?.trim());
      return {
        // "Confirmar" vs "Redactar" no es cosmetico: son dos acciones
        // distintas y el usuario necesita saber cual le espera antes de
        // hacer clic.
        label: tieneContenido
          ? `Confirmar «${label}»`
          : `${conIA ? "Redactar" : "Describir"} «${label}»`,
        targetId: moduleAnchorId(pendiente),
        tab: "formular",
        cta: "Ir al módulo",
      };
    }
  }

  const item = firstPendingRequired(project.checklist);
  if (item) {
    return {
      // Un obligatorio se puede desmarcar conservando su documento (issue
      // #572): ahi lo que falta es validar el requisito, no conseguirlo.
      label: item.linked_document_id
        ? `Marcar «${item.title}» como completo`
        : `Completar «${item.title}»`,
      targetId: CHECKLIST_ANCHOR_ID,
      tab: "radicar",
      cta: "Ir al checklist",
    };
  }

  if (project.status === "draft") {
    return {
      label: "Marcar como Radicado",
      targetId: STATUS_CONTROLS_ANCHOR_ID,
      tab: "radicar",
      cta: "Ir a cambiar estado",
    };
  }

  if (project.bpin_pending) {
    // El BPIN vive en el header, montado en cualquier pestana; "radicar"
    // es la pestana del trabajo al que pertenece.
    return {
      label: "Registrar el BPIN",
      targetId: BPIN_ANCHOR_ID,
      tab: "radicar",
      cta: "Ir al BPIN",
    };
  }

  return null;
}

function findFirstUnconfirmed(formulation: FormulationDTO, keys: ModuleKey[]): ModuleKey | null {
  for (const key of keys) {
    if (formulation.modules[key]?.status !== "confirmed") return key;
  }
  return null;
}

function firstPendingRequired(checklist: ChecklistItemDTO[]): ChecklistItemDTO | null {
  const pendientes = checklist
    .filter((i) => i.is_required && !i.is_completed)
    .sort((a, b) => a.order_index - b.order_index);
  return pendientes[0] ?? null;
}

export interface FocusProgress {
  hechos: number;
  total: number;
  /** Que se esta midiendo, para etiquetarlo sin ambiguedad. */
  etiqueta: string;
  /** De donde sale el numero: la formulacion o el checklist de radicacion. */
  fuente: "formulacion" | "checklist";
}

/**
 * El avance que importa AHORA — uno solo.
 *
 * Antes convivian dos barras: la del checklist en el header y la de
 * formulacion dentro del documento. Se veian parecidas, median cosas
 * distintas y nada decia cual mirar. Mientras haya formulacion pendiente
 * manda esa; cuando el documento esta completo, el trabajo pasa a ser
 * radicar y manda el checklist.
 *
 * `null` cuando no hay nada que contar: sin formulacion y sin obligatorios
 * en el checklist (issue #569, segundo caso: un idea-first radicado sin
 * BPIN cuya formulacion no carga). "0/0 requisitos para radicar" al lado
 * de "Sigue: Registrar el BPIN" era un contador vacio disfrazado de avance;
 * lo que sigue ya lo dice `computeNextAction`.
 */
export function computeFocusProgress(
  project: ProjectDetailDTO,
  formulation: FormulationDTO | null,
): FocusProgress | null {
  const obligatorios = project.checklist.filter((i) => i.is_required);

  if (!formulation && obligatorios.length === 0) return null;

  if (formulation) {
    const confirmado = (k: ModuleKey) => formulation.modules[k]?.status === "confirmed";
    // Cuenta la fase en curso, no el total de 9 (issue #572): la barra decia
    // "0/9" mientras el documento decia "0 de 6 confirmados" y el panel de
    // estado pedia "los 6 módulos de identificación". Primero identificacion
    // (lo que desbloquea preparacion y el cambio de estado), despues
    // preparacion.
    const identificacion = MODULE_ORDER.filter(confirmado).length;
    if (identificacion < MODULE_ORDER.length) {
      return {
        hechos: identificacion,
        total: MODULE_ORDER.length,
        etiqueta: "módulos de identificación",
        fuente: "formulacion",
      };
    }
    // Los idea-first no tienen checklist por diseno (issue #569): con la
    // formulacion completa, "0/0 requisitos para radicar" decia que no
    // habia nada que medir justo cuando acababa de completarla. Si no hay
    // obligatorios, la formulacion sigue siendo el avance real.
    const preparacion = PREPARACION_ORDER.filter(confirmado).length;
    if (preparacion < PREPARACION_ORDER.length || obligatorios.length === 0) {
      return {
        hechos: preparacion,
        total: PREPARACION_ORDER.length,
        etiqueta: "módulos de preparación",
        fuente: "formulacion",
      };
    }
  }

  return {
    hechos: obligatorios.filter((i) => i.is_completed).length,
    total: obligatorios.length,
    etiqueta: "requisitos para radicar",
    fuente: "checklist",
  };
}
